import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { orIlike, pageRange, searchTerms } from '@/lib/list-query'
import type { Database } from '@/types/database'

type AccountInsert = Database['public']['Tables']['accounts']['Insert']
type AccountUpdate = Database['public']['Tables']['accounts']['Update']
type AccountType = Database['public']['Enums']['account_type']
type AccountSubType = Database['public']['Enums']['account_sub_type']

const ACCOUNTS_KEY = ['accounts'] as const

// Only what each surface renders — not select('*').
const ACCOUNT_LIST_COLUMNS =
  'id, name, customer_ref, type, sub_type, is_active, city, state, phone, updated_at, account_divisions(id)'
const ACCOUNT_PICKER_COLUMNS = `${ACCOUNT_LIST_COLUMNS}, ticker_symbol`

// Picker/name-map consumers (command palette, forms, filters, tasks).
// Unpaginated by design; the paginated list surfaces use useAccountsList.
export function useAccounts(showInactive = false) {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, { showInactive }],
    queryFn: async () => {
      let query = supabase
        .from('accounts')
        .select(ACCOUNT_PICKER_COLUMNS)
        .order('name')
      if (!showInactive) query = query.eq('is_active', true)
      const { data, error } = await query
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

// Whitelist of server-sortable columns; anything else falls back to name.
const ACCOUNT_SORT_COLUMNS = ['name', 'type', 'updated_at'] as const

// The default list scope: channel accounts (who we sell through), not the
// end users deals are for. End-user accounts stay reachable via the scope
// control, deal links, and search — this is a view default, not a wall.
export type AccountScope = 'channel' | 'end_users' | 'all'

export const CHANNEL_ACCOUNT_TYPES = ['reseller', 'distributor'] as const satisfies readonly AccountType[]

// An explicit type filter wins over the scope so saved views and shared
// links from before the scope control existed keep returning their rows.
export function accountTypeFilter(
  scope: AccountScope | undefined,
  type: AccountType | undefined,
): { eq?: AccountType; in?: readonly AccountType[] } {
  if (type) return { eq: type }
  if (scope === 'channel') return { in: CHANNEL_ACCOUNT_TYPES }
  if (scope === 'end_users') return { eq: 'end_user' }
  return {}
}

export interface AccountListParams {
  pageIndex: number
  pageSize: number
  sortBy: string
  sortDir: 'asc' | 'desc'
  showInactive: boolean
  scope?: AccountScope
  type?: AccountType
  subType?: AccountSubType
  state?: string
  country?: string
  search?: string
}

export function useAccountsList(params: AccountListParams) {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, 'list', params],
    queryFn: async () => {
      const { from, to } = pageRange(params.pageIndex, params.pageSize)
      let query = supabase
        .from('accounts')
        .select(ACCOUNT_LIST_COLUMNS, { count: 'exact' })
      if (!params.showInactive) query = query.eq('is_active', true)
      const typeFilter = accountTypeFilter(params.scope, params.type)
      if (typeFilter.eq) query = query.eq('type', typeFilter.eq)
      else if (typeFilter.in) query = query.in('type', [...typeFilter.in])
      if (params.subType) query = query.eq('sub_type', params.subType)
      if (params.state) query = query.ilike('state', params.state)
      if (params.country) query = query.ilike('country', params.country)
      if (params.search) {
        for (const term of searchTerms(params.search)) {
          query = query.or(orIlike(['name', 'customer_ref'], term))
        }
      }
      const sortBy = (ACCOUNT_SORT_COLUMNS as readonly string[]).includes(params.sortBy)
        ? params.sortBy
        : 'name'
      const { data, error, count } = await query
        .order(sortBy, { ascending: params.sortDir === 'asc' })
        // stable tie-break so rows can't straddle page boundaries
        .order('id', { ascending: true })
        .range(from, to)
      if (error) throw error
      return { rows: data, total: count ?? 0 }
    },
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  })
}

export function useAccount(id: string) {
  return useQuery({
    queryKey: [...ACCOUNTS_KEY, id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('accounts')
        // parent_account_id is a self-join; hinting via accounts!parent_account_id
        // resolves to the to-many (children) side and yields [] on parentless rows.
        // Embedding by the FK column gives the to-one parent: object | null.
        // The type parser can't infer column embeds, hence overrideTypes.
        .select('*, account_divisions(*), parent:parent_account_id(id,name)')
        .eq('id', id)
        .single()
        .overrideTypes<{ parent: { id: string; name: string } | null }>()
      if (error) throw error
      return data
    },
    staleTime: 30_000,
  })
}

export function useCreateAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['accounts', 'create'],
    mutationFn: async (values: AccountInsert) => {
      const { data, error } = await supabase
        .from('accounts')
        .insert(values)
        .select()
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      toast.success('Account created')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useUpdateAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['accounts', 'update'],
    mutationFn: async (values: AccountUpdate) => {
      const { error } = await supabase
        .from('accounts')
        .update(values)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: [...ACCOUNTS_KEY, id] })
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useDeactivateAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['accounts', 'deactivate'],
    mutationFn: async (reason?: string) => {
      const { error } = await supabase
        .from('accounts')
        .update({ is_active: false, deactivation_reason: reason ?? null })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: [...ACCOUNTS_KEY, id] })
      toast.success('Account marked inactive')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}

export function useReactivateAccount(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ['accounts', 'reactivate'],
    mutationFn: async () => {
      const { error } = await supabase
        .from('accounts')
        .update({ is_active: true })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY })
      void qc.invalidateQueries({ queryKey: [...ACCOUNTS_KEY, id] })
      toast.success('Account reactivated')
    },
    onError: (err: Error) => toast.error(err.message),
  })
}
